import React, { useEffect, useState, Component } from 'react';
import { WorkspaceHub } from './components/options/WorkspaceHub';
import { LoginPage } from './pages/LoginPage';
import { TermsPage } from './pages/TermsPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { SuperadminPage } from './pages/SuperadminPage';
import { userProfiles, UserProfile } from './services/userProfiles';
import { secureStorage, KEYS } from './services/secureStorage';
import { mcpAuth } from './services/mcpAuth';
import { GlobalDebugPanel } from './components/shared/GlobalDebugPanel';
import { DebugProvider, jiraAction, slackAction, confluenceAction } from './lib/debug-console';
import { WorkiumLogo } from './components/brand/WorkiumLogo';
import { useSessionTimeout } from './hooks/useSessionTimeout';
import { SessionTimeoutModal } from './components/shared/SessionTimeoutModal';
// ═══════════════════════════════════════════════
// Error Boundary — catches render crashes and shows the error
// ═══════════════════════════════════════════════
type ReactErrorInfo = {
  componentStack?: string;
};
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ReactErrorInfo | null;
}
class AppErrorBoundary extends Component<{
  children: React.ReactNode;
}, ErrorBoundaryState> {
  constructor(props: {
    children: React.ReactNode;
  }) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }
  static getDerivedStateFromError(error: Error) {
    return {
      hasError: true,
      error
    };
  }
  componentDidCatch(error: Error, errorInfo: ReactErrorInfo) {
    console.error('[AppErrorBoundary] Caught error:', error, errorInfo);
    this.setState({
      errorInfo
    });
  }
  render() {
    if (this.state.hasError) {
      return <div className="min-h-screen bg-slate-950 flex items-center justify-center p-8" data-id="element-0">
          <div className="max-w-lg w-full bg-slate-900 border border-red-900/50 rounded-2xl p-8 shadow-2xl" data-id="element-1">
            <div className="flex flex-col items-center mb-8 border-b border-slate-800 pb-6" data-id="element-2">
              <WorkiumLogo size="lg" className="opacity-50 grayscale mb-6" data-id="element-3" />
              <div className="flex items-center gap-3" data-id="element-4">
                <div className="w-10 h-10 rounded-xl bg-red-950 border border-red-900/50 flex items-center justify-center text-red-400 text-xl" data-id="element-5">
                  ⚠️
                </div>
                <h1 className="text-xl font-bold text-red-400" data-id="element-6">System Error</h1>
              </div>
            </div>
            <p className="text-sm text-slate-300 mb-4 text-center" data-id="element-7">
              Algo salió mal al renderizar la aplicación. Detalles del error:
            </p>
            <div className="bg-slate-950 border border-slate-800 rounded-lg p-4 mb-4 overflow-auto max-h-48" data-id="element-8">
              <p className="text-xs font-mono text-red-400 whitespace-pre-wrap break-all" data-id="element-9">
                {this.state.error?.message || 'Error desconocido'}
              </p>
              {this.state.error?.stack && <p className="text-[10px] font-mono text-slate-600 mt-2 whitespace-pre-wrap break-all" data-id="element-10">
                  {this.state.error.stack.split('\n').slice(0, 8).join('\n')}
                </p>}
            </div>
            {this.state.errorInfo?.componentStack && <div className="bg-slate-950 border border-slate-800 rounded-lg p-4 mb-4 overflow-auto max-h-32" data-id="element-11">
                <p className="text-[10px] font-semibold text-slate-500 mb-1" data-id="element-12">
                  Component Stack:
                </p>
                <p className="text-[10px] font-mono text-slate-600 whitespace-pre-wrap" data-id="element-13">
                  {this.state.errorInfo.componentStack.split('\n').slice(0, 6).join('\n')}
                </p>
              </div>}
            <button onClick={() => {
            this.setState({
              hasError: false,
              error: null,
              errorInfo: null
            });
            window.location.reload();
          }} className="w-full bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold py-2.5 rounded-xl transition-all" data-id="element-14">
              Recargar aplicación
            </button>
          </div>
        </div>;
    }
    return this.props.children;
  }
}
// ═══════════════════════════════════════════════
// Debug Console SDK Config
// ═══════════════════════════════════════════════
const debugConfig = {
  maxLogs: 200,
  sources: {
    jira: {
      bg: 'bg-blue-500/20',
      text: 'text-blue-400'
    },
    github: {
      bg: 'bg-purple-500/20',
      text: 'text-purple-400'
    },
    google: {
      bg: 'bg-amber-500/20',
      text: 'text-amber-400'
    },
    notion: {
      bg: 'bg-slate-200',
      text: 'text-slate-800'
    },
    slack: {
      bg: 'bg-emerald-500/20',
      text: 'text-emerald-400'
    },
    agent: {
      bg: 'bg-indigo-500/20',
      text: 'text-indigo-400'
    },
    system: {
      bg: 'bg-slate-800',
      text: 'text-slate-400'
    }
  },
  actions: [jiraAction({
    createIssue: async log => {
      console.log('Creating Jira ticket for:', log.tool);
    }
  }), confluenceAction(), slackAction({
    sendMessage: async log => {
      console.log('Sending to Slack:', log.tool);
    }
  })]
};
// ═══════════════════════════════════════════════
// Main App
// ═══════════════════════════════════════════════
export function App() {
  // ─── Public Routes (no auth required) ─────────────────────────────
  const pathname = window.location.pathname;
  // Internal Superadmin Route
  if (pathname.startsWith('/superadmin')) {
    return <AppErrorBoundary data-id="element-15">
        <DebugProvider config={debugConfig} data-id="element-16">
          <SuperadminPage data-id="element-17" />
          <GlobalDebugPanel data-id="element-18" />
        </DebugProvider>
      </AppErrorBoundary>;
  }
  if (pathname === '/terms') {
    return <AppErrorBoundary data-id="element-19">
        <TermsPage data-id="element-20" />
      </AppErrorBoundary>;
  }
  if (pathname === '/privacy') {
    return <AppErrorBoundary data-id="element-21">
        <PrivacyPage data-id="element-22" />
      </AppErrorBoundary>;
  }
  // ─── Authenticated App ────────────────────────────────────────────
  const [activeUser, setActiveUser] = useState<UserProfile | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  useEffect(() => {
    // Check for active session on mount
    const user = userProfiles.getActiveUser();
    if (user) {
      applyUserSettings(user);
      setActiveUser(user);
    }
    setIsInitializing(false);
  }, []);
  const applyUserSettings = (user: UserProfile) => {
    // ═══════════════════════════════════════════════
    // CRITICAL: Clear ALL cached MCP tokens FIRST
    // This forces re-authentication with the new user's credentials
    // ═══════════════════════════════════════════════
    secureStorage.clearAll();
    mcpAuth.clearTokens();
    // ═══════════════════════════════════════════════
    // MCP Cognito credentials (for API Gateway auth)
    // These are SEPARATE from Atlassian API tokens!
    // Cognito authenticates the client → API Gateway → Lambda → Jira
    // ═══════════════════════════════════════════════
    if (user.mcpCredentials?.jira) {
      secureStorage.set(KEYS.JIRA_USER, user.mcpCredentials.jira.username);
      secureStorage.set(KEYS.JIRA_PASS, user.mcpCredentials.jira.password);
      console.log(`[App] Set MCP Jira Cognito credentials for ${user.name}`);
    } else {
      console.warn(`[App] No MCP Jira credentials for ${user.name} — Live mode will not work for Jira tools`);
    }
    if (user.mcpCredentials?.confluence) {
      secureStorage.set(KEYS.CONFLUENCE_USER, user.mcpCredentials.confluence.username);
      secureStorage.set(KEYS.CONFLUENCE_PASS, user.mcpCredentials.confluence.password);
      console.log(`[App] Set MCP Confluence Cognito credentials for ${user.name}`);
    }
    if (user.mcpCredentials?.github) {
      secureStorage.set(KEYS.GITHUB_USER, user.mcpCredentials.github.username);
      secureStorage.set(KEYS.GITHUB_PASS, user.mcpCredentials.github.password);
      console.log(`[App] Set MCP GitHub Cognito credentials for ${user.name}`);
    }
    if (user.mcpCredentials?.slack) {
      secureStorage.set(KEYS.SLACK_USER, user.mcpCredentials.slack.username);
      secureStorage.set(KEYS.SLACK_PASS, user.mcpCredentials.slack.password);
      console.log(`[App] Set MCP Slack Cognito credentials for ${user.name}`);
    }
    if (user.mcpCredentials?.notion) {
      secureStorage.set(KEYS.NOTION_USER, user.mcpCredentials.notion.username);
      secureStorage.set(KEYS.NOTION_PASS, user.mcpCredentials.notion.password);
      console.log(`[App] Set MCP Notion Cognito credentials for ${user.name}`);
    }
  };
  const handleLogin = (profile: UserProfile) => {
    applyUserSettings(profile);
    setActiveUser(profile);
  };
  const handleLogout = () => {
    userProfiles.logout();
    secureStorage.clearAll();
    mcpAuth.clearTokens();
    setActiveUser(null);
  };
  // ─── Session Timeout (15 min inactivity) ──────────────────────────
  const {
    showWarning,
    secondsRemaining,
    extendSession
  } = useSessionTimeout({
    onTimeout: handleLogout,
    enabled: !!activeUser
  });
  // ─── Loading State ────────────────────────────────────────────────
  if (isInitializing) {
    return <div className="min-h-screen bg-slate-950 flex items-center justify-center" data-id="element-23">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" data-id="element-24" />
      </div>;
  }
  // ─── Not Logged In ────────────────────────────────────────────────
  if (!activeUser) {
    return <AppErrorBoundary data-id="element-25">
        <DebugProvider config={debugConfig} data-id="element-26">
          <LoginPage onLogin={handleLogin} data-id="element-27" />
          <GlobalDebugPanel data-id="element-28" />
        </DebugProvider>
      </AppErrorBoundary>;
  }
  // ─── Logged In ────────────────────────────────────────────────────
  return <AppErrorBoundary data-id="element-29">
      <DebugProvider config={debugConfig} data-id="element-30">
        <WorkspaceHub onLogout={handleLogout} activeUser={activeUser} data-id="element-31" />
        <GlobalDebugPanel data-id="element-32" />
        {showWarning && <SessionTimeoutModal secondsRemaining={secondsRemaining} onExtend={extendSession} onLogout={handleLogout} data-id="element-33" />}
      </DebugProvider>
    </AppErrorBoundary>;
}